import type { FramingConfig } from "building-types/framing/framingMetaDataTypes";
import type { LumberProfileType } from "building-types/framing/lumberTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { Table } from "common/data/table";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import type { SpanDirection } from "./floorComponentsTypes";

export interface FramingOverrideFields {
  readonly assemblyId?: ForeignEntityId<"FRAME_FLOOR_ASSEMBLY" | "SLAB_FLOOR_ASSEMBLY">;
  readonly spanDirection?: SpanDirection;
  readonly framingAngle?: number;
  readonly framingStartPt?: Vec2;
}

/** Default thickness for split lines in inches (3.5" = standard framing beam width, 2x4 lumber). */
export const DEFAULT_SPLIT_LINE_THICKNESS = 3.5;
export const DEFAULT_SPLIT_LINE_SPLICE_LENGTH = 1.75;

export enum SplitterType {
  MANUAL_LINE_SPLITTER = "MANUAL_LINE_SPLITTER",
  WALL_LINE_SPLITTER = "WALL_LINE_SPLITTER",
  BEAM_LINE_SPLITTER = "BEAM_LINE_SPLITTER",
}

export enum SplitLineJoistAlignment {
  FLUSH_TOP = "FLUSH_TOP",
  FLUSH_BOTTOM = "FLUSH_BOTTOM",
  DROPPED = "DROPPED",
}

export enum SplitLineSupportFramingCondition {
  RUN_OVER = "RUN_OVER",
  LAP_JOINT = "LAP_JOINT",
}

export interface SplitLineEdgeOverride {
  readonly thickness?: number;
  readonly framingComponentId?: ForeignEntityId<"FRAMING_COMPONENT">;
  readonly framingProfile?: LumberProfileType;
  readonly joistAlignment?: SplitLineJoistAlignment;
  readonly supportFramingCondition?: SplitLineSupportFramingCondition;
  readonly spliceLength?: number;
}

export type SplitLineEdgeOverridesTable = Table<SplitLineEdgeOverride>;

export interface SplitLineBase<T extends SplitterType> {
  readonly id: string;
  readonly splitterType: T;
  readonly polyline: PolyLine2;
  readonly edgeIds: string[];
  readonly edgeOverrides?: SplitLineEdgeOverridesTable;
}

export type ManualLineSplitter = SplitLineBase<SplitterType.MANUAL_LINE_SPLITTER>;
export type WallLineSplitter = SplitLineBase<SplitterType.WALL_LINE_SPLITTER>;
export type BeamLineSplitter = SplitLineBase<SplitterType.BEAM_LINE_SPLITTER>;

export type SplitLine = ManualLineSplitter | WallLineSplitter | BeamLineSplitter;

export interface BayFramingEdgeOverride {
  readonly framingConfig?: FramingConfig;
  /** When set, this bay edge is derived from a split line and its framing properties
   *  are inherited from the split line edge rather than the parent floor edge. */
  readonly derivedFromSplitLineEdge?: string;
}

export type BayFramingEdgeOverridesTable = Table<BayFramingEdgeOverride>;

export interface FloorBay extends FramingOverrideFields {
  readonly id: string;
  readonly parentBayId?: string;
  readonly bayEdgeOverrides?: BayFramingEdgeOverridesTable;
  /**
   * A guaranteed-interior point computed via pole-of-inaccessibility.
   * Serves dual purpose: (1) stable identity key for matching generated bay
   * polygons to existing bay definitions across regenerations (the point is
   * tested for containment inside each candidate polygon), and (2) label
   * position for rendering bay labels/annotations in the editor.
   * Bays are assumed non-overlapping so this point uniquely identifies a bay.
   */
  readonly interiorPoint: Vec2;
}

export interface FloorSplitData {
  readonly splitLines: Table<SplitLine>;
  readonly bays: Table<FloorBay>;
}

/** Empty split data for floors with no bays or split lines. */
export const emptyFloorSplitData: FloorSplitData = { splitLines: {}, bays: {} };
